import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getTenantSlug } from "./tenant";

/**
 * Cart context — localStorage-backed, scoped per tenant.
 * One-time orders only (subscribe stays single-product on ProductDetail).
 *
 * Shape of an item:
 *   { id, variant_id, lineId, name, price, mrp, effective_price, image_url, pack_size, brand_id?, qty, updatedAt }
 *
 * `id`              = product id
 * `variant_id`      = selected ProductVariant id, or null for the default variant (back-compat)
 * `lineId`          = `${id}::${variant_id}` (or just `id` when variant_id is null) — the actual
 *                      dedupe/lookup key, so two variants of the same product can be separate lines
 * `updatedAt`       = Date.now() of the last add/qty change — lets a product card showing multiple
 *                      variant lines pick "the most recently touched one" for its compact summary
 * `price`           = current selling price (post product-level MRP discount)
 * `mrp`             = original MRP (falls back to price if not set)
 * `effective_price` = per-unit price after customer-specific personal discount (falls back to
 *                     `price` when no personal discount is active). The backend now resolves
 *                     the discount rule once per product and applies it to every variant's own
 *                     price too (see routes_customer.py), so this stays correct for any variant,
 *                     not just the default one.
 */
const CartCtx = createContext(null);

const lineIdOf = (productId, variantId) => variantId ? `${productId}::${variantId}` : productId;

const storageKey = () => `fd_cart_${getTenantSlug() || "default"}`;

function loadInitial() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try { localStorage.setItem(storageKey(), JSON.stringify(items)); } catch {}
  }, [items]);

  // `variant` is optional — omit it (as the product grid's quick-add does
  // today) to add the product's default variant, unchanged from before.
  const add = useCallback((product, qty = 1, variant = null) => {
    const variantId = variant?.id ?? null;
    const lineId = lineIdOf(product.id, variantId);
    const price = variant ? variant.price : product.price;
    const mrp = variant ? (variant.mrp ?? variant.price) : (product.mrp ?? product.price);
    const packSize = variant ? (variant.pack_size || product.pack_size) : product.pack_size;
    setItems((cur) => {
      const existing = cur.find((x) => x.lineId === lineId);
      if (existing) {
        // Refresh price fields on subsequent adds — the effective_price
        // could have changed since the last add (discount added/removed).
        return cur.map((x) => x.lineId === lineId ? {
          ...x,
          qty: x.qty + qty,
          price,
          mrp,
          effective_price: variantId ? (variant.effective_price ?? price) : (product.effective_price ?? product.price),
          updatedAt: Date.now(),
        } : x);
      }
      return [...cur, {
        id: product.id,
        variant_id: variantId,
        lineId,
        name: product.name,
        price,
        mrp,
        effective_price: variantId ? (variant.effective_price ?? price) : (product.effective_price ?? product.price),
        image_url: (variant?.image_url) || product.image_url,
        pack_size: packSize,
        brand_id: product.brand_id,
        qty,
        updatedAt: Date.now(),
      }];
    });
  }, []);

  // `id` here is a lineId (== product id for default-variant lines, which
  // is what every existing caller passes — back-compat preserved).
  const setQty = useCallback((id, qty) => {
    setItems((cur) => {
      if (qty <= 0) return cur.filter((x) => x.lineId !== id);
      return cur.map((x) => x.lineId === id ? { ...x, qty, updatedAt: Date.now() } : x);
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((cur) => cur.filter((x) => x.lineId !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, total, mrpTotal, sellingTotal, personalDiscount } = useMemo(() => {
    const c = items.reduce((a, b) => a + (b.qty || 0), 0);
    const mrpT = items.reduce((a, b) => a + (b.qty || 0) * (b.mrp || b.price || 0), 0);
    const sellingT = items.reduce((a, b) => a + (b.qty || 0) * (b.price || 0), 0);
    const finalT = items.reduce((a, b) => {
      const unit = (b.effective_price != null ? b.effective_price : b.price) || 0;
      return a + (b.qty || 0) * unit;
    }, 0);
    return {
      count: c,
      total: finalT,
      mrpTotal: mrpT,
      sellingTotal: sellingT,
      personalDiscount: Math.max(0, sellingT - finalT),
    };
  }, [items]);

  return (
    <CartCtx.Provider value={{
      items, add, setQty, remove, clear,
      count, total, mrpTotal, sellingTotal, personalDiscount,
    }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
